/**
 * @file TwoFactorSteps.tsx
 * @brief Two-step verification screens (B31, ADR-059):
 *        - `TwoFactorSignInStep`: what Login and the one-tap email link page
 *          show after a sign-in answered TWO_FACTOR_REQUIRED (code screen) or
 *          TWO_FACTOR_SETUP_REQUIRED (setup-only screen).
 *        - `SetupSteps`: QR + setup key + first code, shared with
 *          Profile → Security.
 *        - `CodeField`: the 6-digit / backup code box.
 *
 * @details
 * - Code screen: "Enter the 6-digit code from your authenticator app",
 *   "Use a backup code", and "Trust this device for 30 days" only when the
 *   server allows it (never for the Owner).
 * - Setup-only: QR, setup key, first code, then the 10 backup codes shown
 *   once; the session starts only after "I have saved my backup codes".
 * - The pending / setup tokens stay in AuthContext memory; codes are never
 *   stored or logged.
 *
 * @references
 * - docs/product/two-step-verification.md
 *
 * @author DES
 * @date 25-10-2026
 */

import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from "react";
import { Check, Copy, KeyRound, LoaderCircle, ShieldCheck, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { ApiClientError } from "@/shared/api/client";
import { useAuth, type TwoFactorSetupInfo } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import type { AuthUser } from "@/shared/types/api";
import { BackupCodesPanel } from "./BackupCodesPanel";
import { describeTwoFactorError } from "./twoFactorApi";
import {
  cleanAppCode,
  cleanBackupCode,
  isTwoFactorRestart,
  setupReasonText,
} from "./twoFactorRules";

function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs text-destructive"
    >
      {children}
    </div>
  );
}

export function CodeField({
  id,
  label,
  backup = false,
  value,
  onChange,
  autoFocus = true,
  disabled,
  inputRef,
}: {
  id: string;
  label: string;
  backup?: boolean;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  /** To put the cursor back after a wrong code (the box is disabled while checking). */
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  return (
    <FormFieldGroup label={label} htmlFor={id} icon={backup ? <KeyRound /> : <Smartphone />}>
      <Input
        ref={inputRef}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        disabled={disabled}
        autoComplete={backup ? "off" : "one-time-code"}
        inputMode={backup ? "text" : "numeric"}
        spellCheck={false}
        autoCapitalize="off"
        maxLength={backup ? 32 : 7}
        placeholder={backup ? "xxxxx-xxxxx" : "123 456"}
        className="font-mono tracking-widest"
      />
    </FormFieldGroup>
  );
}

// ----------------------------------------------------------------------
// Setup: QR + key + first code
// ----------------------------------------------------------------------

/**
 * Starts the setup once on mount (StrictMode runs effects twice; a second
 * start would replace the secret behind the QR already on screen), shows the
 * QR and key, then sends the first code to `confirm`.
 */
export function SetupSteps({
  start,
  confirm,
  confirmLabel = "Turn on",
}: {
  start: () => Promise<TwoFactorSetupInfo>;
  /** The first code, plus the setup shown (its `accountName` labels the codes file). */
  confirm: (code: string, info: TwoFactorSetupInfo) => Promise<void>;
  confirmLabel?: string;
}) {
  const startRef = useRef<Promise<TwoFactorSetupInfo> | null>(null);
  const [info, setInfo] = useState<TwoFactorSetupInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const codeId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = () => {
    setLoadError(null);
    startRef.current = start();
    return startRef.current;
  };

  useEffect(() => {
    let alive = true;
    const p = startRef.current ?? load();
    p.then(
      (v) => alive && setInfo(v),
      (err) => alive && setLoadError(describeTwoFactorError(err)),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, []);

  const retry = () => {
    setInfo(null);
    setError(null);
    setCode("");
    load().then(setInfo, (err) => setLoadError(describeTwoFactorError(err)));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = cleanAppCode(code);
    if (!info) return;
    if (!clean) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirm(clean, info);
    } catch (err) {
      setError(describeTwoFactorError(err));
      if (err instanceof ApiClientError && err.code === "TWO_FACTOR_SETUP_NOT_STARTED") retry();
      else window.setTimeout(() => inputRef.current?.focus(), 0);
    } finally {
      setBusy(false);
    }
  };

  const copyKey = async () => {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(info.setupKey.replace(/\s+/g, ""));
      setKeyCopied(true);
      window.setTimeout(() => setKeyCopied(false), 2000);
    } catch {
      // the key is selectable; nothing else to do
    }
  };

  if (loadError) {
    return (
      <div className="flex flex-col gap-3">
        <ErrorLine>{loadError}</ErrorLine>
        <Button type="button" variant="secondary" onClick={retry}>
          Try again
        </Button>
      </div>
    );
  }
  if (!info) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <LoaderCircle className="h-4 w-4 animate-spin" />
        Getting your QR code…
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <ol className="flex flex-col gap-4 text-sm">
        <li>
          <span className="font-medium">1. Scan this QR code</span>
          <span className="block text-muted-foreground">
            Use an authenticator app such as Google Authenticator, Microsoft Authenticator or 1Password.
          </span>
          {/* The QR stays black on white in dark mode: phone cameras need the contrast. */}
          <img
            src={info.qrCodeDataUrl}
            alt="QR code for your authenticator app"
            className="mx-auto mt-3 h-44 w-44 rounded-xl bg-white p-2"
          />
          <span className="mt-3 block text-xs text-muted-foreground">Can&rsquo;t scan it? Enter this key in the app:</span>
          <span className="mt-1 flex items-center gap-2">
            <code className="min-w-0 flex-1 select-all break-all rounded-xl bg-app-card px-3 py-2 font-mono text-[13px] tracking-wider">
              {info.setupKey}
            </code>
            <Button type="button" variant="ghost" size="icon" aria-label="Copy the setup key" onClick={() => void copyKey()}>
              {keyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </span>
        </li>
        <li>
          <span className="font-medium">2. Enter the 6-digit code the app shows</span>
        </li>
      </ol>
      <CodeField
        id={codeId}
        label="Code"
        value={code}
        onChange={setCode}
        autoFocus={false}
        disabled={busy}
        inputRef={inputRef}
      />
      {error ? <ErrorLine>{error}</ErrorLine> : null}
      <Button type="submit" className="h-12 w-full text-[15px] font-semibold" disabled={busy || !code.trim()}>
        {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : confirmLabel}
      </Button>
    </form>
  );
}

// ----------------------------------------------------------------------
// Sign-in: code screen / setup-only screen
// ----------------------------------------------------------------------

function StepHeader({ title, body }: { title: string; body: string }) {
  return (
    <div className="mb-6 text-center">
      <ShieldCheck className="mx-auto mb-3 h-8 w-8 text-brand" strokeWidth={1.75} />
      <h2 className="text-2xl font-bold tracking-tight text-foreground">{title}</h2>
      <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

/**
 * Renders the second step for `useAuth().twoFactorChallenge`; nothing when
 * there is none. `onSignedIn` runs once a session exists, with the page a
 * one-tap link named (B33; null for every other sign-in).
 */
export function TwoFactorSignInStep({
  onSignedIn,
  onCancel,
  backLabel,
}: {
  onSignedIn: (user: AuthUser, redirect: string | null) => void;
  /** After "Back to sign in" (the challenge is already dropped). */
  onCancel?: () => void;
  /** "Back to sign in" by default; a delete screen says "Cancel" (B33). */
  backLabel?: string;
}) {
  const { twoFactorChallenge: challenge, cancelTwoFactor } = useAuth();
  if (!challenge) return null;
  const back = () => {
    cancelTwoFactor();
    onCancel?.();
  };
  return challenge.kind === "code" ? (
    <CodeStep
      key={challenge.pendingToken}
      trustAllowed={challenge.trustDeviceAllowed}
      onSignedIn={onSignedIn}
      onBack={back}
      backLabel={backLabel}
    />
  ) : (
    <SetupOnlyStep
      key={challenge.setupToken}
      reason={setupReasonText(challenge.reason)}
      onSignedIn={onSignedIn}
      onBack={back}
      backLabel={backLabel}
    />
  );
}

function BackLink({ onBack, disabled, label }: { onBack: () => void; disabled?: boolean; label?: string }) {
  return (
    <Button
      type="button"
      variant="ghost"
      className="h-12 w-full rounded-full bg-app-card text-[15px] font-semibold hover:bg-app-quaternary"
      onClick={onBack}
      disabled={disabled}
    >
      {label ?? "Back to sign in"}
    </Button>
  );
}

function CodeStep({
  trustAllowed,
  onSignedIn,
  onBack,
  backLabel,
}: {
  trustAllowed: boolean;
  onSignedIn: (user: AuthUser, redirect: string | null) => void;
  onBack: () => void;
  backLabel?: string;
}) {
  const { verifyTwoFactor } = useAuth();
  const [backup, setBackup] = useState(false);
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const codeId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = backup ? cleanBackupCode(code) : cleanAppCode(code);
    if (!clean) {
      setError(backup ? "Enter one of your backup codes." : "Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { user, backupCodesLeft, redirect } = await verifyTwoFactor(
        backup ? { backupCode: clean, trustDevice: trust } : { code: clean, trustDevice: trust },
      );
      if (backupCodesLeft != null) {
        toast({
          title: `${backupCodesLeft} backup ${backupCodesLeft === 1 ? "code" : "codes"} left`,
          description:
            backupCodesLeft <= 3 ? "Make new ones in Profile → Security → Two-step verification." : undefined,
        });
      }
      onSignedIn(user, redirect);
    } catch (err) {
      setError(describeTwoFactorError(err));
      if (err instanceof ApiClientError && isTwoFactorRestart(err.code)) setOver(true);
      setCode("");
      setBusy(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <div className="flex flex-col items-stretch">
      <StepHeader
        title="Two-step verification"
        body={
          backup
            ? "Enter one of the backup codes you saved when you turned this on."
            : "Enter the 6-digit code from your authenticator app."
        }
      />
      <form onSubmit={onSubmit} className="space-y-3.5" noValidate>
        {!over ? (
          <>
            <CodeField
              key={backup ? "backup" : "app"}
              id={codeId}
              label={backup ? "Backup code" : "Code"}
              backup={backup}
              value={code}
              onChange={setCode}
              disabled={busy}
              inputRef={inputRef}
            />
            {trustAllowed ? (
              <label className="flex items-center gap-2.5 px-1 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand"
                  checked={trust}
                  onChange={(e) => setTrust(e.target.checked)}
                  disabled={busy}
                />
                Trust this device for 30 days
              </label>
            ) : null}
          </>
        ) : null}
        {error ? <ErrorLine>{error}</ErrorLine> : null}
        {!over ? (
          <>
            <Button type="submit" className="mt-1 h-12 w-full text-[15px] font-semibold" disabled={busy || !code.trim()}>
              {busy ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  Checking…
                </>
              ) : (
                "Verify"
              )}
            </Button>
            <button
              type="button"
              className="block w-full py-1 text-center text-sm font-medium text-brand hover:underline"
              onClick={() => {
                setBackup((v) => !v);
                setCode("");
                setError(null);
              }}
              disabled={busy}
            >
              {backup ? "Use a code from your app" : "Use a backup code"}
            </button>
          </>
        ) : null}
        <BackLink onBack={onBack} disabled={busy} label={backLabel} />
      </form>
      {!over ? (
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Lost your phone and your backup codes? Contact VERBLIZR support to reset it.
        </p>
      ) : null}
    </div>
  );
}

function SetupOnlyStep({
  reason,
  onSignedIn,
  onBack,
  backLabel,
}: {
  reason: string;
  onSignedIn: (user: AuthUser, redirect: string | null) => void;
  onBack: () => void;
  backLabel?: string;
}) {
  const { startChallengeSetup, confirmChallengeSetup } = useAuth();
  const [done, setDone] = useState<{
    backupCodes: string[];
    redirect: string | null;
    finish: () => Promise<AuthUser>;
    accountName: string | null;
  } | null>(null);
  const [finishing, setFinishing] = useState(false);

  if (done) {
    return (
      <div className="flex flex-col items-stretch">
        <StepHeader title="Save your backup codes" body="Two-step verification is on." />
        <BackupCodesPanel
          codes={done.backupCodes}
          accountName={done.accountName}
          continueLabel="Continue to VERBLIZR"
          busy={finishing}
          onContinue={async () => {
            setFinishing(true);
            try {
              onSignedIn(await done.finish(), done.redirect);
            } catch {
              setFinishing(false);
              toast({ variant: "destructive", title: "Couldn't sign you in", description: "Two-step verification is on. Sign in again." });
              onBack();
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-stretch">
      <StepHeader title="Set up two-step verification" body={`${reason} It takes a minute.`} />
      <SetupSteps
        start={startChallengeSetup}
        confirm={async (code, info) =>
          setDone({ ...(await confirmChallengeSetup(code)), accountName: info.accountName ?? null })
        }
        confirmLabel="Turn on and continue"
      />
      <div className="mt-3.5">
        <BackLink onBack={onBack} label={backLabel} />
      </div>
    </div>
  );
}
